<template>
	<div class="rotating-banner-container">
		<transition name="fade" mode="out-in">
			<master-classes-promo-banner
				v-if="currentBannerIndex === 0"
				key="master-classes"
			/>
			<creators-club-promo-banner-compact
				v-else-if="currentBannerIndex === 1 && shouldShowCreatorsClubBanner"
				key="creators-club"
			/>
			<new-user-promo-promo-banner-compact
				v-else-if="currentBannerIndex === 2 && shouldShowNewUserPromoBanner"
				key="new-user-promo"
			/>
		</transition>

		<!-- Optional: Banner indicators -->
		<div class="banner-indicators" v-if="showIndicators">
			<div
				v-for="(banner, index) in availableBanners"
				:key="index"
				class="indicator"
				:class="{ 'active': index === currentBannerIndex }"
				@click="setCurrentBanner(index)"
			></div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue';
import MasterClassesPromoBanner from './MasterClassesPromoBanner.vue';
import CreatorsClubPromoBannerCompact from './CreatorsClubPromoBannerCompact.vue';
import NewUserPromoPromoBannerCompact from './NewUserPromoPromoBannerCompact.vue';
import { mapState } from 'vuex';

export default Vue.extend({
	name: 'RotatingBannerSystem',
	components: {
		MasterClassesPromoBanner,
		CreatorsClubPromoBannerCompact,
		NewUserPromoPromoBannerCompact,
	},
	props: {
		rotationInterval: {
			type: Number,
			default: 7000, // 7 seconds
		},
		showIndicators: {
			type: Boolean,
			default: true,
		},
		showAllBanners: {
			type: Boolean,
			default: false,
		},
	},
	data () {
		return {
			currentBannerIndex: 0,
			rotationTimer: null,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'loadingUser',
			'loadingTeam',
		]),
		shouldShowCreatorsClubBanner () {
			// Show if showAllBanners is true (for testing) or if conditions are met
			return this.showAllBanners || (
				!this.loadingUser &&
				!this.loadingTeam &&
				!this._get(this.team, 'isActive') &&
				this._get(this.user, 'clubOffer.isFreemium')
			);
		},
		shouldShowNewUserPromoBanner () {
			// Show if showAllBanners is true (for testing) or if new user promo conditions are met
			return this.showAllBanners || this.shouldNewUserPromoOpen;
		},
		shouldNewUserPromoOpen () {
			// Simplified version of the logic from App.vue
			// This would need to be implemented based on the actual conditions
			return false; // For now, set to false until we implement the full logic
		},
		availableBanners () {
			const banners = ['MasterClasses'];

			if (this.shouldShowCreatorsClubBanner) {
				banners.push('CreatorsClub');
			}

			if (this.shouldShowNewUserPromoBanner) {
				banners.push('NewUserPromo');
			}

			return banners;
		},
		totalBanners () {
			return this.availableBanners.length;
		}
	},
	watch: {
		totalBanners(newTotal) {
			// If current index is out of bounds, reset to 0
			if (this.currentBannerIndex >= newTotal) {
				this.currentBannerIndex = 0;
			}
		}
	},
	mounted () {
		this.startRotation();
	},
	beforeDestroy () {
		this.stopRotation();
	},
	methods: {
		startRotation () {
			this.rotationTimer = setInterval(() => {
				this.nextBanner();
			}, this.rotationInterval);
		},
		stopRotation () {
			if (this.rotationTimer) {
				clearInterval(this.rotationTimer);
				this.rotationTimer = null;
			}
		},
		nextBanner () {
			if (this.totalBanners > 0) {
				this.currentBannerIndex = (this.currentBannerIndex + 1) % this.totalBanners;
			}
		},
		setCurrentBanner (index) {
			if (index >= 0 && index < this.totalBanners) {
				this.currentBannerIndex = index;
				// Reset the timer when manually changing banners
				this.stopRotation();
				this.startRotation();
			}
		},
	},
});
</script>

<style scoped lang="scss">
.rotating-banner-container {
	position: relative;
	width: 100%;
	height: 160px; // Fixed height to prevent shifting
	z-index: 0;

	// Mobile height match
	@media (max-width: 959px) {
		height: 200px;
	}
}

.banner-indicators {
	position: absolute;
	bottom: 12px;
	right: 35px;
	display: flex;
	gap: 6px;
	z-index: 30;
}

.indicator {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: rgba(255, 255, 255, 0.4);
	cursor: pointer;
	transition: background-color 0.3s ease;

	&.active {
		background-color: rgba(255, 255, 255, 0.8);
	}

	&:hover {
		background-color: rgba(255, 255, 255, 0.6);
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s ease;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}
</style>
